import React, { useCallback, useState } from 'react'
import { Collapse, Image, Progress, Upload, message } from 'antd';
import Tooltip from '../../common/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro'
import { useTranslation } from 'react-i18next'
import { useStore } from '../../../store/store'
import { uploadDocumentMedia } from '../../../requests/mediaUpload'
import { MAX_MEDIA_SIZE, MAX_MEDIA_SIZE_LABEL } from '../../../shared/media/mediaLimits'
import {
  MEDIA_CONTEXTS, defaultResolutionFor, mediaSelection,
} from '../../../shared/media/mediaResolution'
import { OVERLAY_Z_INDEX_CEILING, PRIMARY_DARK, getPrimaryDark } from '../../../constants/constants'
import DuotoneLightIcon from '../../common/DuotoneLightIcon'
import MediaBankPicker from '../../Image/MediaBankPicker'

// Images prédéfinies d'une variable image : stockées sur la box elle-même
// (predefinedValues), donc dans le document — définies dans un modèle, elles
// suivent chaque proposition qui en est issue. Seules les URL sont conservées :
// les fichiers sont hébergés par l'API image du document, comme un upload
// classique de box image.
const FormImagePredefinedValues = ({ box }) => {
  const { t } = useTranslation()
  const updateBox = useStore(({ updateBox }) => updateBox)
  const currentDocument = useStore(({ currentDocument }) => currentDocument)
  const currentProposal = useStore(({ currentProposal }) => currentProposal)
  // Une variable peut vivre dans un masque (en-tête / pied de page) : la box
  // n'est alors pas dans `boxes`.
  const predefinedValues = useStore(({ boxes, masks }) =>
    [...boxes, ...masks.map((m) => m.boxes).flat()].find((b) => b.id === box.id)?.predefinedValues
  )
  const [isUploading, setIsUploading] = useState(false)
  const [percent, setPercent] = useState(null)

  const isDisabled = box.from_linked_section
  const accentColor = getPrimaryDark(currentDocument?.type === 'template')
  const values = (predefinedValues ?? []).filter((value) => !!value?.url)

  const addValue = useCallback((value) => {
    updateBox(box.id, (b) => {
      const current = b.predefinedValues ?? []
      if (current.some((existing) => existing?.url === value.url)) return
      b.predefinedValues = [...current, value]
    })
  }, [box.id, updateBox])

  const removeValue = useCallback((url) => {
    updateBox(box.id, (b) => {
      b.predefinedValues = (b.predefinedValues ?? []).filter((value) => value?.url !== url)
    })
  }, [box.id, updateBox])

  const handleUpload = useCallback(async ({ file }) => {
    if (file.size > MAX_MEDIA_SIZE) {
      message.error({
        content: `La taille du fichier ne doit pas dépasser ${MAX_MEDIA_SIZE_LABEL}`,
        key: 'file-too-large',
      })
      return
    }
    setIsUploading(true)
    setPercent(0)
    try {
      const documentId = currentDocument.type === 'template' ? currentDocument.id : currentProposal.id
      const { selection } = await uploadDocumentMedia({
        documentId,
        documentType: currentDocument.type,
        file,
        onProgress: setPercent,
      })
      addValue({
        url: selection.url,
        filename: file.name,
        mediaId: selection.mediaId,
        resolution: selection.resolution,
      })
      // L'image est rangée dans le dossier auto du document : rafraîchir la banque.
      useStore.getState().bumpMediaBank()
    } catch (err) {
      message.error({ content: "Erreur lors de l'upload de l'image", key: 'upload-error' })
    } finally {
      setIsUploading(false)
      setPercent(null)
    }
  }, [addValue, currentDocument, currentProposal])

  // flexShrink: 0 : la section est un item flex de la colonne du formulaire.
  return (
    <div style={{ marginBottom: 16, flexShrink: 0 }}>
      <div style={{ marginBottom: 8 }}>{t('predefined-values')}</div>
      {values.length > 0 &&
        // PreviewGroup : la prévisualisation d'une vignette peut naviguer vers
        // les suivantes plutôt que de rester isolée à une seule image.
        <Image.PreviewGroup>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 8 }}>
            {values.map(({ url, filename }) => (
              <div key={url} style={thumbWrapperStyle}>
                <Image
                  src={url}
                  alt={filename}
                  wrapperStyle={{ width: '100%', height: '100%' }}
                  style={thumbImgStyle}
                  // Icône seule, sans le texte "Preview" par défaut : l'app n'a
                  // pas de locale antd française configurée.
                  // zIndex : sans lui l'aperçu prend le défaut (bas) d'antd et
                  // passe sous la barre flottante d'une box sélectionnée ailleurs
                  // sur le canvas (qui peut monter jusqu'à 99999 dans cette app).
                  preview={{
                    mask: <FontAwesomeIcon icon={icon({ name: 'eye', style: 'solid' })} />,
                    zIndex: OVERLAY_Z_INDEX_CEILING,
                  }}
                />
                {!isDisabled &&
                  <Tooltip title={t('delete')} mouseLeaveDelay="0">
                    <FontAwesomeIcon
                      icon={icon({ name: 'circle-xmark', style: 'solid' })}
                      onClick={() => removeValue(url)}
                      style={removeIconStyle}
                    />
                  </Tooltip>
                }
              </div>
            ))}
          </div>
        </Image.PreviewGroup>
      }
      {!isDisabled &&
        <>
          {/* Hauteur imposée : Upload.Dragger est en height 100% côté antd, il
              occuperait sinon toute la colonne et pousserait la banque hors écran. */}
          <div style={{ height: 96 }}>
            <Upload.Dragger
              accept="image/png, image/gif, image/jpeg"
              name="file"
              customRequest={handleUpload}
              showUploadList={false}
              disabled={isUploading}
              className="image-dropzone"
            >
              <p style={{ margin: 0 }}>
                <DuotoneLightIcon name="cloud-arrow-up" weight="solid" style={{ fontSize: 22, color: accentColor }} />
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: PRIMARY_DARK }}>
                {t('drop-image')}
              </p>
            </Upload.Dragger>
          </div>
          {/* Sans elle, un envoi de plusieurs dizaines de mégaoctets paraît planté. */}
          {percent != null && <Progress percent={percent} size="small" status="active" />}
          <Collapse
            ghost
            size="small"
            items={[{
              key: 'bank',
              label: <span style={{ fontSize: 12, fontWeight: 600, color: accentColor }}>{t('image-bank')}</span>,
              children: (
                // La banque peut contenir beaucoup d'images : on la borne et on
                // scrolle dedans plutôt que d'étirer la colonne du formulaire.
                <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                  {/* Contexte PDF : le palier `pdf`, pas l'original — ces valeurs
                      finissent dans un document imprimé à 1600 px. */}
                  <MediaBankPicker
                    onSelectMedia={(media) => {
                      const selection = mediaSelection(
                        media,
                        defaultResolutionFor(media, MEDIA_CONTEXTS.pdf),
                      )
                      addValue({
                        url: selection.url,
                        filename: media.filename || selection.url.split('/').pop(),
                        mediaId: selection.mediaId,
                        resolution: selection.resolution,
                      })
                    }}
                  />
                </div>
              ),
            }]}
          />
        </>
      }
    </div>
  )
}

const thumbWrapperStyle = {
  position: 'relative',
  height: 64,
  border: '1px solid rgba(11,34,57,0.12)',
  borderRadius: 6,
  background: '#fafafa',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}

const thumbImgStyle = { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }

const removeIconStyle = {
  position: 'absolute',
  top: 2,
  right: 2,
  // Au-dessus du masque de survol posé par l'aperçu antd (Image), sinon un
  // clic dans son coin ouvrirait l'aperçu au lieu de supprimer la vignette.
  zIndex: 1,
  cursor: 'pointer',
  color: '#ff4d4f',
  background: 'white',
  borderRadius: '50%',
}

export default FormImagePredefinedValues
